import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Protocol } from '../../types';

interface ProtocolState {
  protocols: Protocol[];
  currentProtocol: Protocol | null;
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  currentPage: number;
  total: number;
  filters: {
    search: string;
    category: string;
    status: string;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
  };
}

const initialState: ProtocolState = {
  protocols: [],
  currentProtocol: null,
  isLoading: false,
  error: null,
  hasMore: true,
  currentPage: 1,
  total: 0,
  filters: {
    search: '',
    category: '',
    status: '',
    sortBy: 'title',
    sortOrder: 'asc',
  },
};

const protocolSlice = createSlice({
  name: 'protocols',
  initialState,
  reducers: {
    setProtocols: (state, action: PayloadAction<Protocol[]>) => {
      state.protocols = action.payload;
    },
    addProtocols: (state, action: PayloadAction<Protocol[]>) => {
      state.protocols = [...state.protocols, ...action.payload];
    },
    setCurrentProtocol: (state, action: PayloadAction<Protocol | null>) => {
      state.currentProtocol = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setTotal: (state, action: PayloadAction<number>) => {
      state.total = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<ProtocolState['filters']>>) => {
      // Check if filters actually changed
      const filtersChanged =
        (action.payload.search !== undefined && action.payload.search !== state.filters.search) ||
        (action.payload.category !== undefined &&
          action.payload.category !== state.filters.category) ||
        (action.payload.status !== undefined && action.payload.status !== state.filters.status) ||
        (action.payload.sortBy !== undefined && action.payload.sortBy !== state.filters.sortBy) ||
        (action.payload.sortOrder !== undefined &&
          action.payload.sortOrder !== state.filters.sortOrder);

      state.filters = { ...state.filters, ...action.payload };

      // Only reset if filters actually changed
      if (filtersChanged) {
        state.currentPage = 1; // Reset to first page when filters change
        state.protocols = []; // Clear protocols when filters change
      }
    },
    resetProtocols: state => {
      state.protocols = [];
      state.currentPage = 1;
      state.hasMore = true;
      state.error = null;
    },
  },
});

export const {
  setProtocols,
  addProtocols,
  setCurrentProtocol,
  setLoading,
  setError,
  setHasMore,
  setCurrentPage,
  setTotal,
  setFilters,
  resetProtocols,
} = protocolSlice.actions;
export default protocolSlice.reducer;
